// Catalog review (Phase 1C): broker-flagged catalog issues, human review, and
// a correction export for a separate catalog process.
//
// Read docs/part-intelligence/FEATURE_CHARTER.md before changing anything here.
// Nothing on this page changes the public catalog. Loading it makes no
// PartsBase call: evidence shown is what was stored when each issue was flagged.

import { useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { ChevronRight, Download, Layers } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { useCopilot } from '@/lib/copilot-context';
import {
  exportCatalogCorrections,
  getCatalogFamilies,
  getCatalogReviewQueue,
  recordCatalogReview,
  type CatalogQueueItem,
  type CatalogReviewInput,
} from '@/lib/api';
import {
  CORRECTION_LABEL,
  CORRECTION_TYPES,
  FAMILY_RULES,
  REVIEW_STATUS_LABEL,
  type CorrectionType,
  type ReviewStatus,
} from '@shared/part-intelligence/catalog-review';
import { MACHINE_STATE_LABEL, SEARCH_STATE_LABEL } from '@shared/part-intelligence/decisions';

const QUEUE_KEY = ['catalog-review'] as const;

const STATUS_PILL: Record<ReviewStatus, string> = {
  open: 'pill pill-default',
  needs_evidence: 'pill pill-info',
  confirmed_problem: 'pill pill-warning',
  dismissed: 'pill pill-success',
};
const FAMILY_LABEL = Object.fromEntries(FAMILY_RULES.map((f) => [f.id, f.label]));

type Filter = 'all' | ReviewStatus;

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function CatalogReviewPage() {
  const { getToken } = useAuth();
  const focusLine = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('line') : null;
  const [filter, setFilter] = useState<Filter>('all');
  const [repeated, setRepeated] = useState(false);
  const [inFamily, setInFamily] = useState(false);
  const [provenance, setProvenance] = useState<'any' | 'known' | 'unknown'>('any');
  const [exportError, setExportError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUEUE_KEY,
    queryFn: () => getCatalogReviewQueue(() => getToken()),
    staleTime: 30_000,
    retry: false,
  });
  const status = (error as { status?: number } | null)?.status;

  const items = data?.items ?? [];
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: items.length, open: 0, needs_evidence: 0, confirmed_problem: 0, dismissed: 0 };
    for (const it of items) c[it.status]++;
    return c;
  }, [items]);
  const shown = items.filter((it) =>
    (!focusLine || it.rfqs.some((r) => r.lineItemId === focusLine))
    && (filter === 'all' || it.status === filter)
    && (!repeated || it.rfqCount > 1 || it.flagCount > 1)
    && (!inFamily || it.families.length > 0)
    && (provenance === 'any' || (provenance === 'known') === !!it.catalog));

  const download = async () => {
    setExportError(null);
    try {
      const out = await exportCatalogCorrections(() => getToken());
      const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `catalog-corrections-${out.contentSha256.slice(0, 12)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setExportError((e as { message?: string }).message ?? 'Export failed');
    }
  };

  return (
    <AppShell title="Catalog review" subtitle="Broker-flagged catalog issues for human review. Nothing here changes the public catalog.">
      <div className="h-full overflow-auto px-4 sm:px-6 py-6 space-y-4">
        {isLoading && <div className="card p-8 text-sm text-foreground-muted text-center">Loading…</div>}
        {isError && status === 403 && (
          <div className="card p-8 text-sm text-foreground-muted text-center">Catalog feedback is not enabled for this workspace.</div>
        )}
        {isError && status !== 403 && (
          <div className="card p-8 text-sm text-danger text-center">{(error as { message?: string })?.message ?? 'Failed to load'}</div>
        )}

        {data && (
          <>
            <section className="card p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                {(['all', 'open', 'needs_evidence', 'confirmed_problem', 'dismissed'] as Filter[]).map((f) => (
                  <button key={f} onClick={() => setFilter(f)}
                    className={`px-2 py-1 rounded border ${filter === f ? 'border-primary text-primary-strong bg-surface-muted' : 'border-border hover:bg-muted'}`}>
                    {f === 'all' ? 'All' : REVIEW_STATUS_LABEL[f]} ({counts[f]})
                  </button>
                ))}
                <span className="w-px h-4 bg-border mx-1" />
                <label className="flex items-center gap-1"><input type="checkbox" checked={repeated} onChange={(e) => setRepeated(e.target.checked)} /> Repeated</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={inFamily} onChange={(e) => setInFamily(e.target.checked)} /> Possible family</label>
                <select className="border border-border rounded px-1.5 py-0.5 bg-background" value={provenance}
                  onChange={(e) => setProvenance(e.target.value as typeof provenance)} aria-label="Catalog source">
                  <option value="any">Any catalog source</option>
                  <option value="known">Catalog source established</option>
                  <option value="unknown">Catalog source not established</option>
                </select>
                {data.canReview && (
                  <button onClick={() => void download()} className="ml-auto flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-muted">
                    <Download className="w-3.5 h-3.5" /> Export confirmed corrections ({counts.confirmed_problem})
                  </button>
                )}
              </div>
              {focusLine && (
                <div className="text-xs text-foreground-muted">
                  Showing the issue for one RFQ line. <Link href="/catalog-review" className="text-primary hover:underline">Show all</Link>
                </div>
              )}
              {exportError && <div className="text-xs text-danger">{exportError}</div>}
              {!data.catalogAvailable && (
                <div className="text-xs text-foreground-muted">The catalog table is not available here, so catalog sources cannot be established.</div>
              )}
            </section>

            <FamiliesSection items={items} />

            {shown.length === 0 ? (
              <div className="card p-8 text-sm text-foreground-muted text-center">
                {items.length === 0 ? 'No catalog issues have been flagged. Brokers flag them from an RFQ line’s Part identity panel.' : 'Nothing matches these filters.'}
              </div>
            ) : (
              <ul className="space-y-2">
                {shown.map((it) => (
                  <IssueCard key={it.issueKey} item={it} canReview={data.canReview} defaultOpen={!!focusLine} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function FamiliesSection({ items }: { items: CatalogQueueItem[] }) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['catalog-review', 'families'],
    enabled: open,
    queryFn: () => getCatalogFamilies(() => getToken()),
    staleTime: 10 * 60_000,
  });
  return (
    <section className="card overflow-hidden">
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-surface-muted/60">
        <span className="flex items-center gap-2 font-medium"><Layers className="w-4 h-4 text-primary-strong" /> Possible families</span>
        <ChevronRight className={`w-4 h-4 text-foreground-muted transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-xs space-y-2">
          <p className="text-foreground-muted">
            Deterministic patterns from the Phase 0 discovery. A family is a reason to look, not a verdict: Phase 0 found real
            parts in the same shapes. Membership changes no review status, and there is no bulk decision.
          </p>
          {isLoading && <div className="text-foreground-muted">Counting the catalog…</div>}
          {data?.families.map((f) => (
            <div key={f.id} className="border border-border rounded p-2">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{f.label}</span>
                <span className="text-foreground-muted">
                  {f.catalogCount === null ? 'catalog not available' : `${f.catalogCount.toLocaleString()} catalog pages`}
                  {' · '}{items.filter((i) => i.families.includes(f.id)).length} flagged in this queue
                </span>
              </div>
              <div className="text-foreground-muted">{f.description}</div>
              {f.samples.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {f.samples.map((s) => <span key={s.path} className="font-mono" title={s.path}>{s.partNumber}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function IssueCard({ item, canReview, defaultOpen }: { item: CatalogQueueItem; canReview: boolean; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const ev = item.evidence;
  return (
    <li className="card overflow-hidden">
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full text-left px-4 py-3 hover:bg-surface-muted/50">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-sm font-medium">{item.catalog?.partNumber || item.identifier}</span>
          <span className={STATUS_PILL[item.status]}>{REVIEW_STATUS_LABEL[item.status]}</span>
          {(item.rfqCount > 1 || item.flagCount > 1) && <span className="pill pill-info">{item.flagCount} flags · {item.rfqCount} RFQs</span>}
          {item.families.map((f) => <span key={f} className="pill pill-default" title="Possible family: a review aid only">{FAMILY_LABEL[f] ?? f}</span>)}
          <ChevronRight className={`ml-auto w-4 h-4 text-foreground-muted transition-transform ${open ? 'rotate-90' : ''}`} />
        </div>
        <div className="mt-1 text-xs text-foreground-muted [overflow-wrap:anywhere]">
          {item.catalog ? <>Catalog page <span className="font-mono text-foreground">{item.catalog.path}</span></> : 'Catalog source not established'}
          {' · '}flagged {fmtDate(item.lastFlagged)}{item.flaggedBy.length ? ` by ${item.flaggedBy.join(', ')}` : ''}
        </div>
        <div className="mt-1 flex flex-wrap gap-1 text-xs">
          {ev.machineState && <span className="pill pill-default">When flagged: {MACHINE_STATE_LABEL[ev.machineState as keyof typeof MACHINE_STATE_LABEL] ?? ev.machineState}</span>}
          {ev.flisState && <span className="pill pill-default">FLIS · {SEARCH_STATE_LABEL[ev.flisState as keyof typeof SEARCH_STATE_LABEL] ?? ev.flisState}{ev.flisEdition ? ` (${ev.flisEdition})` : ''}</span>}
          {ev.partsbaseState && <span className="pill pill-default">PartsBase · {SEARCH_STATE_LABEL[ev.partsbaseState as keyof typeof SEARCH_STATE_LABEL] ?? ev.partsbaseState}{ev.partsbaseBasis === 'loose_key' ? ' (spelling unverified)' : ''}</span>}
          {ev.candidate && <span className="pill pill-default">Candidate <span className="font-mono">{ev.candidate}</span></span>}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 text-xs space-y-3 border-t border-border">
          <AskCopilotAboutIssue issueKey={item.issueKey} />
          <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-3 gap-y-1 pt-2">
            <div className="text-foreground-muted">Flagged identifier</div>
            <div className="font-mono [overflow-wrap:anywhere]">{item.entered.length ? item.entered.join(' · ') : '—'}</div>
            <div className="text-foreground-muted">Lookup key</div>
            <div className="font-mono">{item.identifier}</div>
            {item.catalog && <>
              <div className="text-foreground-muted">Catalog record</div>
              <div className="[overflow-wrap:anywhere]"><span className="font-mono">{item.catalog.partNumber}</span>
                {item.catalog.manufacturer ? ` · listed manufacturer “${item.catalog.manufacturer}” (may be the seller)` : ''}</div>
            </>}
            <div className="text-foreground-muted">Observed</div>
            <div>{fmtDate(item.firstFlagged)} to {fmtDate(item.lastFlagged)}</div>
          </div>

          {item.rfqs.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-foreground-muted mb-1">RFQs that exposed it</div>
              <ul className="space-y-0.5">
                {item.rfqs.map((r) => (
                  <li key={r.lineItemId}>
                    <Link href={`/rfqs/${r.rfqId}`} className="text-primary hover:underline">{r.ref}</Link>
                    <span className="text-foreground-muted"> · flagged as <span className="font-mono">{r.entered}</span>
                      {r.lineNow !== r.entered && <> · line now <span className="font-mono">{r.lineNow}</span></>} · {fmtDate(r.flaggedAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.notes.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-foreground-muted mb-1">Broker notes</div>
              {item.notes.map((n, i) => <div key={i} className="text-foreground-muted">“{n}”</div>)}
            </div>
          )}

          <ReviewHistory item={item} />
          {canReview ? <ReviewForm item={item} /> : <div className="text-foreground-muted">Reviews are recorded by owners and admins.</div>}
        </div>
      )}
    </li>
  );
}

function ReviewHistory({ item }: { item: CatalogQueueItem }) {
  if (item.history.length === 0) return <div className="text-foreground-muted">Not reviewed yet.</div>;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-foreground-muted mb-1">Review history</div>
      <ul className="space-y-0.5">
        {item.history.map((r) => (
          <li key={r.id} className="text-foreground-muted">
            {fmtDate(r.reviewedAt)} · {r.reviewedBy ?? 'a reviewer'} · <span className="text-foreground">
              {r.value === 'reopened' ? 'Reopened' : REVIEW_STATUS_LABEL[r.value]}</span>
            {r.correctionType ? ` · ${CORRECTION_LABEL[r.correctionType]}` : ''}
            {r.replacementIdentifier ? ` → ${r.replacementIdentifier}` : ''}
            {r.note ? ` · “${r.note}”` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewForm({ item }: { item: CatalogQueueItem }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [value, setValue] = useState<CatalogReviewInput['value'] | ''>('');
  const [correction, setCorrection] = useState<CorrectionType | ''>('');
  const [replacement, setReplacement] = useState('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: (input: CatalogReviewInput) => recordCatalogReview(input, () => getToken()),
    onSuccess: () => {
      setValue(''); setCorrection(''); setReplacement(''); setNote('');
      void qc.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });
  const ready = value !== '' && (value !== 'confirmed_problem' || correction !== '')
    && (correction !== 'replacement_identifier' || replacement.trim() !== '');
  const submit = () => {
    if (!ready) return;
    const input: CatalogReviewInput = { issueKey: item.issueKey, value: value as CatalogReviewInput['value'] };
    if (value === 'confirmed_problem') {
      input.correctionType = correction as CorrectionType;
      if (replacement.trim()) input.replacementIdentifier = replacement.trim();
    }
    if (note.trim()) input.note = note.trim();
    save.mutate(input);
  };
  const options: Array<[CatalogReviewInput['value'], string]> = [
    ['needs_evidence', REVIEW_STATUS_LABEL.needs_evidence],
    ['confirmed_problem', REVIEW_STATUS_LABEL.confirmed_problem],
    ['dismissed', REVIEW_STATUS_LABEL.dismissed],
    ...(item.status !== 'open' ? [['reopened', 'Reopen'] as [CatalogReviewInput['value'], string]] : []),
  ];
  return (
    <div className="border border-border rounded p-3 space-y-2 bg-background">
      <div className="text-[11px] uppercase tracking-wide text-foreground-muted">Record a review</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map(([v, label]) => (
          <label key={v} className="flex items-center gap-1.5">
            <input type="radio" name={`rv-${item.issueKey}`} checked={value === v} onChange={() => setValue(v)} /> {label}
          </label>
        ))}
      </div>
      {value === 'confirmed_problem' && (
        <div className="flex flex-wrap gap-2 items-center">
          <select className="border border-border rounded px-1.5 py-0.5 bg-background" value={correction}
            onChange={(e) => setCorrection(e.target.value as CorrectionType)} aria-label="Correction type">
            <option value="">Correction type…</option>
            {CORRECTION_TYPES.map((c) => <option key={c} value={c}>{CORRECTION_LABEL[c]}</option>)}
          </select>
          <input className="font-mono border border-border rounded px-1.5 py-0.5 min-w-0 flex-1" maxLength={100} value={replacement}
            onChange={(e) => setReplacement(e.target.value)}
            placeholder={correction === 'replacement_identifier' ? 'Corrected identifier (required)' : 'Corrected identifier (optional)'} />
        </div>
      )}
      <textarea className="w-full border border-border rounded px-1.5 py-1 bg-background" rows={2} maxLength={2000}
        value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Rationale (optional). A confirmed problem's note is included in the correction export." />
      <div className="flex items-center gap-2">
        <button className="px-2 py-1 border border-border rounded hover:bg-muted font-medium disabled:opacity-50" disabled={!ready || save.isPending} onClick={submit}>
          Save review
        </button>
        <span className="text-foreground-muted">Records a decision only. The public catalog is not changed.</span>
      </div>
      {save.error && <div className="text-danger">{(save.error as { message?: string }).message ?? 'Could not save'}</div>}
    </div>
  );
}

/** Research the flagged identifier with APD Copilot (pilot tenants only). */
function AskCopilotAboutIssue({ issueKey }: { issueKey: string }) {
  const { capability, ask } = useCopilot();
  if (!capability.canUse) return null;
  return (
    <button className="mt-2 px-2 py-1 border border-border rounded hover:bg-muted"
      onClick={() => ask({ scope: { type: 'catalog_issue', ref: issueKey }, text: 'Research this identifier.', mode: 'research', autoSend: true })}>
      Research with Copilot
    </button>
  );
}
